// apps/console/src/services/api/robot-telemetry-store.ts
//
// Latest telemetry reading per robot. Mirrors robot-positions-store / robot-alerts-store: a
// module-level listener Set plus a snapshot, fed by useLiveChannel and read by useRobotTelemetry().
//
// This exists because the DashboardBridge's `robot:telemetry` event forwards only `payload.metrics`
// — the shape an AGIBOT robot sends. A helmet sends its reading as the payload itself (battery,
// worn, vitals, gas, signal), so every one of those fields was being dropped on the way to the UI.
// Keeping the whole payload here means a device that reports something we have not modelled yet is
// still shown rather than silently discarded.

/** One robot's most recent telemetry, kept verbatim as the device reported it. */
export interface RobotTelemetryItem {
  robotId: string
  /** The full telemetry payload. Field names vary by device kind — read defensively. */
  data: Record<string, unknown>
  /** When this reading was received by the browser. */
  receivedAt: number
}

type Listener = (byRobot: Record<string, RobotTelemetryItem>) => void

const listeners = new Set<Listener>()
let byRobot: Record<string, RobotTelemetryItem> = {}

function notify(): void {
  // Fresh object identity so React state setters see a change.
  const snapshot = { ...byRobot }

  listeners.forEach(l => l(snapshot))
}

/** Upsert one robot's latest reading (newest wins). */
export function emitRobotTelemetry(robotId: string, data: Record<string, unknown>): void {
  if (!robotId) return

  byRobot = { ...byRobot, [robotId]: { robotId, data, receivedAt: Date.now() } }
  notify()
}

/** Subscribe; the current map is pushed synchronously so first render is correct. */
export function subscribeRobotTelemetry(listener: Listener): () => void {
  listeners.add(listener)
  listener({ ...byRobot })

  return () => {
    listeners.delete(listener)
  }
}

export function __resetRobotTelemetryForTests(): void {
  byRobot = {}
  listeners.clear()
}
